import { useEffect, useRef, useState } from 'react'

import AnimatedScore from '@/components/AnimatedScore/AnimatedScore'
import Team from '@/components/Team/Team'
import { useTheme } from '@/contexts/ThemeContext'
import { useResolution } from '@/hooks/useResolution'
import { useTemplateAnimation } from '@/hooks/useTemplateAnimation'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { hexToRgba } from '@/utils/colorUtils'
import './BaseballBar.css'

import type { JsonMatchData, BaseballScore, BaseballEventState } from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { FC } from 'react'

export interface BaseballBarProps {
  matchData?: MatchData
  jsonData: JsonMatchData | null
  isVisible?: boolean
}

const MULTIPLIERS = {
  scoreFontSize: 1.3,
  inningFontSize: 0.85,
  outsFontSize: 0.6,
  baseSize: 0.45,
  diamondSize: 1.3,
}

const BaseballBar: FC<BaseballBarProps> = ({ jsonData, isVisible = true }) => {
  const theme = useTheme()
  const resolution = useResolution()
  const animationRef = useTemplateAnimation(isVisible, { animationType: 'slideUp', duration: 0.5 })

  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)

  const homeScore = homeCompetitor?.score as BaseballScore | undefined
  const awayScore = awayCompetitor?.score as BaseballScore | undefined

  const homeRuns = homeScore?.runs ?? 0
  const awayRuns = awayScore?.runs ?? 0

  const eventState = jsonData?.eventState as BaseballEventState | undefined
  const inning = eventState?.inning ?? 1
  const inningState = eventState?.inningState ?? 'TOP'
  const outs = eventState?.outs ?? 0
  const bases = eventState?.bases ?? [false, false, false]

  const isFinal = eventState?.isFinal ?? false
  const isInInnings = !isFinal && (inningState === 'TOP' || inningState === 'BOT')
  const awayIsBatting = inningState === 'TOP'

  const inningLabel = isFinal ? 'FINAL' : `${inningState} ${inning}`
  const inningArrow = inningState === 'TOP' ? '▲' : '▼'

  // Fade label out/in when text changes between end-innings states
  const [displayedLabel, setDisplayedLabel] = useState(inningLabel)
  const [labelFading, setLabelFading] = useState(false)
  const fadingRef = useRef(false)

  useEffect(() => {
    if (inningLabel === displayedLabel) return
    // Start fade out
    setLabelFading(true)
    fadingRef.current = true
    const timeout = setTimeout(() => {
      // Swap text and fade back in
      setDisplayedLabel(inningLabel)
      setLabelFading(false)
      fadingRef.current = false
    }, 300) // matches CSS transition duration
    return () => {
      clearTimeout(timeout)
      if (fadingRef.current) {
        setDisplayedLabel(inningLabel)
        setLabelFading(false)
        fadingRef.current = false
      }
    }
  }, [inningLabel, displayedLabel])

  const primaryBg = hexToRgba(theme.colors.primary, 0.99)
  const whiteBg = 'rgba(255, 255, 255, 0.99)'
  const battingTextColor = theme.colors.primary

  // Gradient stop positions — white section covers batting side + center
  const whiteStart = isInInnings ? (awayIsBatting ? '0%' : '42.5%') : '50%'
  const whiteEnd = isInInnings ? (awayIsBatting ? '57.5%' : '100%') : '50%'

  // Text colours per side
  const awayText = isInInnings && awayIsBatting ? battingTextColor : 'white'
  const homeText = isInInnings && !awayIsBatting ? battingTextColor : 'white'
  const centerText = isInInnings ? battingTextColor : 'white'

  // Shared card style — CSS variables drive the gradient in both rows
  const cardStyle = {
    width: resolution.height,
    '--bar-row-height': `calc(${theme.baseHeight} * 1.667)`,
    '--primary-bg': primaryBg,
    '--white-bg': whiteBg,
    '--white-start': whiteStart,
    '--white-end': whiteEnd,
  } as React.CSSProperties

  // Trapezoid clip-path for branding row
  const trapP = `calc(${theme.baseHeight} * 0.6)`
  const brandingClipPath = `polygon(calc(35% + ${trapP}) 0, calc(65% - ${trapP}) 0, 65% 100%, 35% 100%)`

  const renderMiniDiamond = () => {
    const diamondSize = `calc(${theme.baseHeight} * ${MULTIPLIERS.diamondSize})`
    const baseSize = `calc(${theme.baseHeight} * ${MULTIPLIERS.baseSize})`
    const halfBase = `calc(${theme.baseHeight} * ${MULTIPLIERS.baseSize / 2})`

    return (
      <div className="baseball-bar-diamond" style={{ width: diamondSize, height: diamondSize }}>
        {/* 2nd base - top center */}
        <div
          className={`baseball-bar-base${bases[1] ? ' baseball-bar-base--occupied' : ''}`}
          style={{
            width: baseSize,
            height: baseSize,
            top: 0,
            left: `calc(50% - ${halfBase})`,
          }}
        />
        {/* 3rd base - middle left */}
        <div
          className={`baseball-bar-base${bases[2] ? ' baseball-bar-base--occupied' : ''}`}
          style={{
            width: baseSize,
            height: baseSize,
            top: `calc(50% - ${halfBase})`,
            left: 0,
          }}
        />
        {/* 1st base - middle right */}
        <div
          className={`baseball-bar-base${bases[0] ? ' baseball-bar-base--occupied' : ''}`}
          style={{
            width: baseSize,
            height: baseSize,
            top: `calc(50% - ${halfBase})`,
            right: 0,
          }}
        />
      </div>
    )
  }

  return (
    <div ref={animationRef} className="baseball-bar-content-wrapper">
      <div className="baseball-bar-card baseball-bar-grid" style={cardStyle} data-scorebar-panel>
        {/* Row 1: Branding */}
        <div className="baseball-bar-branding-row" style={{ clipPath: brandingClipPath, height: theme.baseHeight }}>
          <div />
          <div className="baseball-bar-branding-center">
            <img
              src={isInInnings ? '/assets/frogbox-logo.png' : '/assets/frogbox-logo-mono.png'}
              alt="FrogBox"
              style={{ height: `calc(${theme.baseHeight} * 0.5)` }}
            />
          </div>
          <div />
        </div>

        {/* Row 2: Bar */}
        <div className="baseball-bar-row">
          <div className="baseball-bar-side" style={{ color: awayText }}>
            <Team data={awayCompetitor} style={{ paddingRight: 0 }} />
            <AnimatedScore
              value={awayRuns}
              className="baseball-bar-score"
              style={{
                fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreFontSize})`,
                marginLeft: 'auto',
                marginRight: `calc(${theme.baseHeight} * 1)`,
              }}
            />
          </div>

          {/* Center — both contents always in DOM, CSS toggles visibility */}
          <div
            className="baseball-bar-center-group"
            style={{
              color: centerText,
              gap: `calc(${theme.baseHeight} * 0.9)`,
              padding: `0 calc(${theme.baseHeight} * 0.5)`,
            }}
          >
            {/* In-innings content: diamond + outs */}
            <div
              className={`baseball-bar-innings-content${isInInnings ? '' : ' baseball-bar-innings-content--hidden'}`}
            >
              {renderMiniDiamond()}
              <div
                className="baseball-bar-state-info"
                style={{ transform: `translateY(calc(${theme.baseHeight} * -0.15))` }}
              >
                <span
                  className="baseball-bar-inning-compact"
                  style={{
                    fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.inningFontSize})`,
                    transform: `translateY(calc(${theme.baseHeight} * 0.15))`,
                  }}
                >
                  <span
                    style={
                      inningState === 'BOT'
                        ? { fontSize: '0.8em', lineHeight: '1.5em', paddingRight: '0.2em' }
                        : undefined
                    }
                  >
                    {inningArrow}
                  </span>
                  {inning}
                </span>
                <span
                  className="baseball-bar-outs"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.outsFontSize})` }}
                >
                  {outs} OUT
                </span>
              </div>
            </div>

            {/* End-innings content: label (MID/END/FINAL) */}
            <span
              className={`baseball-bar-inning-label${isInInnings || labelFading ? ' baseball-bar-inning-label--hidden' : ''}`}
              style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.inningFontSize})` }}
            >
              {displayedLabel}
            </span>
          </div>

          <div className="baseball-bar-side" style={{ color: homeText }}>
            <AnimatedScore
              value={homeRuns}
              className="baseball-bar-score"
              style={{
                fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreFontSize})`,
                marginLeft: `calc(${theme.baseHeight} * 1)`,
              }}
            />
            <Team data={homeCompetitor} isHome style={{ paddingLeft: 0, marginLeft: 'auto' }} align="right" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default BaseballBar
